/**
 * Patient Billing API - patient-scoped charges, claims, payments, and balance.
 *
 * Backed by ehr-go/internal/modules/billing routes:
 * - GET  /patients/:id/billing/charges
 * - POST /billing/charges
 * - POST /billing/charges/:id/void
 * - GET  /patients/:id/billing/claims
 * - POST /billing/claims
 * - PUT  /billing/claims/:id
 * - GET  /patients/:id/billing/payments
 * - POST /billing/payments
 * - GET  /patients/:id/billing/balance
 */
import { apiGet, apiPost, apiPut } from "./client";
import type { ClaimSnapshot } from "@/lib/claim-snapshot-compare";
import type {
  BillingCharge,
  BillingClaim,
  BillingListResponse,
  BillingPayment,
  PatientBalance,
} from "@/types";

export interface BillingClaimLineItem {
  id: string;
  line_number: number;
  procedure_code: string;
  code_system: string;
  description: string;
  modifier_1?: string | null;
  modifier_2?: string | null;
  diagnosis_pointers?: string | null;
  quantity: number;
  charge: number; // cents
  service_date?: string | null; // YYYY-MM-DD
  created_at: string;
}

/** GET /billing/claims/:id returns a richer payload than the list row. */
export interface BillingClaimDetail extends BillingClaim {
  organization_id: string;
  practitioner_id: string;
  facility_id?: string | null;
  insurance_policy_id?: string | null;
  place_of_service?: string | null;
  allowed_amount: number; // cents
  paid_amount: number; // cents
  adjustment: number; // cents
  notes?: string | null;
  diagnosis_codes?: string | null; // JSON string in backend
  line_items?: BillingClaimLineItem[];
  active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ClaimReviewPatientSummary {
  id: string;
  mrn?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string; // YYYY-MM-DD
  gender?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

export interface ClaimReviewInsuranceCompanySummary {
  id: string;
  name: string;
  payer_id?: string | null;
  inbound_payer_number?: string | null;
  sharp_revenue_payer_id?: string | null;
  claims_enabled: boolean;
  eligibility_enabled: boolean;
}

export interface ClaimReviewInsurancePolicySummary {
  id: string;
  policy_type: string;
  status: string;
  policy_number?: string;
  subscriber_id?: string | null;
  subscriber_first_name?: string | null;
  subscriber_last_name?: string | null;
  relationship_to_subscriber?: string | null;
  group_number?: string | null;
  plan_name?: string | null;
  insurance_company?: ClaimReviewInsuranceCompanySummary | null;
}

export interface ClaimReviewFacilitySummary {
  id: string;
  name: string;
  pos_code?: string | null;
  taxonomy?: string | null;
}

export interface ClaimReviewBillingProviderSummary {
  organization_id: string;
  name: string;
  npi?: string | null;
  tax_id_last4?: string;
  phone?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}

export interface ClaimReviewRenderingProviderSummary {
  id: string;
  first_name?: string;
  last_name?: string;
  npi?: string | null;
  taxonomy?: string | null;
}

export interface BillingClaimReviewResponse {
  claim: BillingClaimDetail;
  patient?: ClaimReviewPatientSummary | null;
  insurance_policy?: ClaimReviewInsurancePolicySummary | null;
  facility?: ClaimReviewFacilitySummary | null;
  billing_provider?: ClaimReviewBillingProviderSummary | null;
  rendering_provider?: ClaimReviewRenderingProviderSummary | null;
}

export interface ClaimReworkMessage {
  id: string;
  claim_id: string;
  author_user_id: string;
  author_name?: string;
  message: string;
  created_at: string;
}

// ── Payloads matching the Go DTOs ─────────────────────────────────────────────

export interface CreateChargePayload {
  encounter_id: string;
  patient_id: string;
  provider_id: string;
  code_type?: "CPT" | "HCPCS" | "ICD-10";
  procedure_code: string;
  description?: string;
  modifier_1?: string;
  units?: number;
  charge: number; // cents
  diagnosis_codes?: string[];
  service_date: string; // YYYY-MM-DD
  notes?: string;
}

export interface CreateClaimPayload {
  encounter_id: string;
  patient_id: string;
  practitioner_id: string;
  facility_id?: string;
  insurance_policy_id?: string;
  secondary_insurance_policy_id?: string;
  claim_type?: "professional" | "institutional";
  form_type?: "CMS-1500" | "UB-04";
  place_of_service?: string;
  service_date: string; // YYYY-MM-DD
  notes?: string;
}

export interface RecordPaymentPayload {
  patient_id: string;
  claim_id?: string;
  amount: number; // cents
  method: "cash" | "check" | "card" | "era" | "eft";
  source?: "patient" | "insurance";
  collection_reason?:
    | "copay"
    | "deductible"
    | "coinsurance"
    | "patient_balance"
    | "primary_insurance"
    | "secondary_insurance"
    | "self_pay"
    | "other";
  reference_number?: string;
  payer_name?: string;
  adjustment_amount?: number;
  adjustment_reason?: string;
  posted_date: string; // YYYY-MM-DD
  deposit_date?: string;
  notes?: string;
}

export function getPatientBalance(patientId: string): Promise<PatientBalance> {
  return apiGet<PatientBalance>(`/patients/${patientId}/billing/balance`);
}

export function listPatientCharges(
  patientId: string,
  params?: { page?: number; per_page?: number; status?: string },
): Promise<BillingListResponse<BillingCharge>> {
  return apiGet<BillingListResponse<BillingCharge>>(
    `/patients/${patientId}/billing/charges`,
    { params },
  );
}

export function listPatientClaims(
  patientId: string,
  params?: { page?: number; per_page?: number; status?: string },
): Promise<BillingListResponse<BillingClaim>> {
  return apiGet<BillingListResponse<BillingClaim>>(
    `/patients/${patientId}/billing/claims`,
    { params },
  );
}

// ── Organization-scoped lists (biller work queue) ───────────────────────────

export function listOrgClaims(
  params?: { page?: number; per_page?: number; status?: string },
): Promise<BillingListResponse<BillingClaim>> {
  return apiGet<BillingListResponse<BillingClaim>>("/billing/claims", { params });
}

export function getOrgClaim(claimId: string): Promise<BillingClaimDetail> {
  return apiGet<BillingClaimDetail>(`/billing/claims/${claimId}`);
}

export function getOrgClaimReview(claimId: string): Promise<BillingClaimReviewResponse> {
  return apiGet<BillingClaimReviewResponse>(`/billing/claims/${claimId}/review`);
}

export function listClaimRework(claimId: string): Promise<ClaimReworkMessage[]> {
  return apiGet<ClaimReworkMessage[]>(`/billing/claims/${claimId}/rework`);
}

export function createClaimRework(claimId: string, body: { message: string }): Promise<ClaimReworkMessage> {
  return apiPost<ClaimReworkMessage>(`/billing/claims/${claimId}/rework`, body);
}

export function listClaimSnapshots(claimId: string): Promise<ClaimSnapshot[]> {
  return apiGet<ClaimSnapshot[]>(`/billing/claims/${claimId}/snapshots`);
}

export function listOrgPayments(
  params?: { page?: number; per_page?: number; method?: string },
): Promise<BillingListResponse<BillingPayment>> {
  return apiGet<BillingListResponse<BillingPayment>>("/billing/payments", { params });
}

export function listPatientPayments(
  patientId: string,
  params?: { page?: number; per_page?: number },
): Promise<BillingListResponse<BillingPayment>> {
  return apiGet<BillingListResponse<BillingPayment>>(
    `/patients/${patientId}/billing/payments`,
    { params },
  );
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function addCharge(
  payload: CreateChargePayload,
): Promise<BillingCharge> {
  return apiPost<BillingCharge>("/billing/charges", payload);
}

export function voidCharge(chargeId: string): Promise<void> {
  return apiPost<void>(`/billing/charges/${chargeId}/void`, {});
}

export function createClaim(
  payload: CreateClaimPayload,
): Promise<BillingClaim> {
  return apiPost<BillingClaim>("/billing/claims", payload);
}

export interface UpdateClaimPayload {
  status?: string;
  denial_reason?: string;
  denial_code?: string;
  claim_number?: string;
  payer_claim_id?: string;
  clearinghouse_id?: string;
  submission_method?: string;
  notes?: string;
}

export function updateClaim(
  claimId: string,
  payload: UpdateClaimPayload,
): Promise<BillingClaim> {
  return apiPut<BillingClaim>(`/billing/claims/${claimId}`, payload);
}

export function recordPayment(
  payload: RecordPaymentPayload,
): Promise<BillingPayment> {
  return apiPost<BillingPayment>("/billing/payments", payload);
}
